"use client";

import { useEffect, useId, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { useTranslations } from "next-intl";
import { z } from "zod";
import { cn } from "@/lib/utils";

const formSchema = z.object({
  title: z.string().trim().min(1).max(100),
  description: z.string().trim().max(500).optional(),
});

export type SaveSnippetFormValues = z.infer<typeof formSchema>;

type SaveSnippetDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: "create" | "edit";
  initialTitle: string;
  initialDescription: string;
  isSubmitting: boolean;
  onSubmit: (values: SaveSnippetFormValues) => void | Promise<void>;
};

export function SaveSnippetDialog({
  open,
  onOpenChange,
  mode,
  initialTitle,
  initialDescription,
  isSubmitting,
  onSubmit,
}: SaveSnippetDialogProps) {
  const t = useTranslations("playground");
  const dialogRef = useRef<HTMLDialogElement>(null);
  const titleInputRef = useRef<HTMLInputElement>(null);
  const headingId = useId();
  const titleFieldId = useId();
  const descFieldId = useId();
  const [title, setTitle] = useState(initialTitle);
  const [description, setDescription] = useState(initialDescription);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setTitle(initialTitle);
      setDescription(initialDescription);
      setError(null);
    }
  }, [open, initialTitle, initialDescription]);

  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    if (open) {
      if (!el.open) el.showModal();
      // The field opens seeded ("Untitled snippet", or the current title), and
      // `showModal` only focuses it. Without the select, the first keystroke
      // appends to the seed instead of replacing it, so a student who types a
      // name gets "Untitled snippetMy solution".
      requestAnimationFrame(() => titleInputRef.current?.select());
    } else if (el.open) {
      el.close();
    }
  }, [open]);

  function handleClose() {
    onOpenChange(false);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const parsed = formSchema.safeParse({
      title,
      description: description.trim() === "" ? undefined : description,
    });
    if (!parsed.success) {
      const first = parsed.error.flatten().fieldErrors.title?.[0];
      setError(first ?? t("saveDialogValidation"));
      return;
    }
    await onSubmit(parsed.data);
  }

  return (
    <dialog
      ref={dialogRef}
      className="w-[min(100vw-2rem,440px)] rounded-xl border border-white/15 bg-[#141416] p-0 text-white shadow-2xl backdrop:bg-black/60"
      onClose={handleClose}
      aria-labelledby={headingId}
    >
      <form onSubmit={(e) => void handleSubmit(e)} className="flex flex-col gap-4 p-5">
        <div>
          <h2 id={headingId} className="text-lg font-semibold text-white">
            {mode === "create" ? t("saveDialogTitleCreate") : t("saveDialogTitleEdit")}
          </h2>
          <p className="mt-1 text-sm text-white/55">{t("saveDialogSubtitle")}</p>
        </div>

        <div>
          <label htmlFor={titleFieldId} className="mb-1 block text-xs font-medium text-white/70">
            {t("saveDialogTitleLabel")}
          </label>
          <input
            id={titleFieldId}
            ref={titleInputRef}
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={100}
            autoComplete="off"
            className="w-full rounded-lg border border-white/15 bg-[#0B0B0C] px-3 py-2 text-sm text-white outline-none ring-[#E94D1D] placeholder:text-white/35 focus:ring-2"
            placeholder={t("saveDialogTitlePlaceholder")}
          />
        </div>

        <div>
          <label htmlFor={descFieldId} className="mb-1 block text-xs font-medium text-white/70">
            {t("saveDialogDescriptionLabel")}
          </label>
          <textarea
            id={descFieldId}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={500}
            rows={3}
            className="w-full resize-y rounded-lg border border-white/15 bg-[#0B0B0C] px-3 py-2 text-sm text-white outline-none ring-[#E94D1D] placeholder:text-white/35 focus:ring-2"
            placeholder={t("saveDialogDescriptionPlaceholder")}
          />
        </div>

        {error && <p className="text-sm text-red-400">{error}</p>}

        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={handleClose}
            className="rounded-lg border border-white/15 px-4 py-2 text-sm font-medium text-white/85 transition hover:bg-white/10"
          >
            {t("saveDialogCancel")}
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className={cn(
              "inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white transition",
              "bg-gradient-to-r from-[#E94D1D] via-[#f05a28] to-[#FFAB8F] hover:brightness-110",
              "disabled:cursor-not-allowed disabled:opacity-50",
            )}
          >
            {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
            {t("saveDialogSubmit")}
          </button>
        </div>
      </form>
    </dialog>
  );
}
